import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { updateMyPassword } from '../../services/api';
import type { OrbitDeckResponse, OrbitOverview, OrbitRoomPerson } from '../../types/aai';
import { OrbitDeck } from '../../components/orbit-deck/OrbitDeck';
import type { DeckAnswer, DeckCardModel, DeckContextItem, DeckContextState, DeckLearned } from '../../components/orbit-deck/types';
import { learnedLineFor, performAnswer, performSkip, railFromOverview, readDocument, searchToolsFor, toDeckCards, transcribeFor } from './deckModel';
import { ingest } from '../../orbit/lib/intakeApi';
import { confirmProblemField, listClientProblems } from '../../orbit/lib/scopeApi';
import { hostOf } from '../../lib/site';
import { ConnectGitPrompt } from '../../components/orbit-deck/ConnectGitPrompt';

const gitPromptKey = (clientId: string | null): string => `orbit_git_prompt_seen:${clientId || ''}`;

/*
 * The problem chapter. After the starter cards, the owner is asked for one
 * pain point in their own words. Naming one creates a problem card on the
 * spot (the same card "+ add a problem" makes), and three short follow-ups
 * fill what the scoping skill asks first. Skipping any of it costs nothing:
 * the card is on Projects under Clarity, and Continue scoping picks it up.
 */
const PROBLEM_PREFIX = 'problem.';
const PAIN_CARD: DeckCardModel = {
  id: 'problem.statement', tag: 'ask', tagLabel: 'One problem to start with', title: 'Any pain point you want fixed?',
  why: 'What goes wrong, who feels it, and what makes it costly. One is enough; more can be added from Projects.',
  input: 'say', placeholder: 'Every Monday someone reads every Jira ticket by hand to write the blocked summary.',
};
const FOLLOW_UP_CARDS: DeckCardModel[] = [
  { id: 'problem.current_state', tag: 'ask', tagLabel: 'About that problem', title: 'How does it work today?',
    why: 'The steps people take now. This is what gets replaced, helped or skipped.', input: 'say', placeholder: 'Who does what, in what order, with which tools.' },
  { id: 'problem.desired_state', tag: 'ask', tagLabel: 'About that problem', title: 'How should it work after?',
    why: 'The happy path. Everything else is exception handling.', input: 'say', placeholder: 'What the new flow looks like when it works.' },
  { id: 'problem.success_metric', tag: 'ask', tagLabel: 'About that problem', title: 'Which number should move?',
    why: 'One metric that can be measured. Without it, success is opinion.', input: 'text', placeholder: 'Hours saved per week, tickets closed per day, errors per month.' },
];
const problemField = (card: DeckCardModel): string => card.id.slice(PROBLEM_PREFIX.length);

/*
 * The deck a signed-in person is dealt. Full-screen, its own topbar, no portal
 * shell: this is where an invite link and a sign-up land. Two chapters. First
 * the starter cards and what Orbit wants confirmed; then, if the person
 * continues, the rest of their dossier. Pick up later is always one click.
 */

type Chapter = 'starter' | 'problem' | 'password' | 'between' | 'rest' | 'done';

/* After the six and the problem, each intake is its own deck, opened by its
   own card, in this order. */
interface IntakeDeck { id: string; label: string; about: string; cards: DeckCardModel[] }
const INTAKE_DECKS: { id: string; label: string; about: string }[] = [
  { id: 'business', label: 'Business intake', about: 'Questions about the company itself.' },
  { id: 'tool', label: 'Tool intake', about: 'Questions about each tool your team uses.' },
  { id: 'department', label: 'Department intake', about: 'Questions about how your department works.' },
  { id: 'person', label: 'Person intake', about: 'Questions about you and your own work.' },
  { id: 'other', label: 'More questions', about: 'What is left for you.' },
];

export const DeckPage: React.FC = () => {
  const navigate = useNavigate();
  const [deck, setDeck] = useState<OrbitDeckResponse | null>(null);
  const [overview, setOverview] = useState<OrbitOverview | null>(null);
  const [people, setPeople] = useState<OrbitRoomPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [learned, setLearned] = useState<DeckLearned[]>([]);
  const [auto, setAuto] = useState(0);
  const [removed, setRemoved] = useState<string[]>([]);
  const [added, setAdded] = useState<DeckContextItem[]>([]);
  const [chapter, setChapter] = useState<Chapter>('starter');
  const [problemId, setProblemId] = useState<string | null>(null);
  const [problemDone, setProblemDone] = useState(false);
  // The stack keeps only the cards its caller still lists, so the follow-ups
  // have to be listed here once the pain point is named, not only handed back
  // from onAnswer: handed back alone, the next render dropped them unanswered.
  const [problemCards, setProblemCards] = useState<DeckCardModel[]>([PAIN_CARD]);
  // The owner is asked once, on the way out, to connect the company's git.
  // Skipped when either provider is already connected or the owner already
  // answered the prompt; it never blocks anyone who is not the owner.
  const [gitPrompt, setGitPrompt] = useState<'unknown' | 'show' | 'done'>('unknown');
  const [gitPromptOpen, setGitPromptOpen] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<string>('/');
  const clientIdForGit = (() => { try { return localStorage.getItem('client_id'); } catch { return null; } })();

  const decideGitPrompt = useCallback(async (ownerRole: boolean) => {
    let seen = false;
    try { seen = localStorage.getItem(gitPromptKey(localStorage.getItem('client_id'))) === '1'; } catch { seen = false; }
    if (!ownerRole || seen) { setGitPrompt('done'); return; }
    try {
      const { data: catalog } = await aaiApi.getConnectors();
      const connected = (catalog.connectors || []).some((c) => (c.slug === 'github' || c.slug === 'gitlab') && c.workspace_git?.connected);
      setGitPrompt(connected ? 'done' : 'show');
    } catch {
      setGitPrompt('done');
    }
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The pain point is asked once per company. A company that already has a
      // problem on the board (from this deck or from Projects) is not asked again.
      const clientId = localStorage.getItem('client_id');
      const problems = clientId ? listClientProblems(clientId).catch(() => []) : Promise.resolve([]);
      const [d, o, found] = await Promise.all([
        aaiApi.getOrbitDeck('me'), aaiApi.getOrbitOverview().catch(() => null), problems,
      ]);
      setDeck(d.data);
      if (o) setOverview(o.data);
      const hasProblem = found.length > 0;
      if (hasProblem) setProblemDone(true);
      // Neither holds up the first card: the Git prompt is only offered once the
      // deck is done, and people only name who handed a card on. The connector
      // catalog alone kept a new Owner waiting seconds on "Dealing your cards".
      const ownerRoleForGit = ['OWNER', 'EXECUTIVE', 'AI_CHAMPION'].includes((d.data.viewer.role || '').toUpperCase());
      void decideGitPrompt(ownerRoleForGit);
      aaiApi.getOrbitRoomPeople().then((p) => setPeople(p.data.people)).catch(() => setPeople([]));
      if (d.data.cards.length === 0) {
        let next: Chapter = 'done';
        if (d.data.rest.length > 0) next = 'between';
        let mustAsk = false;
        try { mustAsk = localStorage.getItem('orbit_set_password') === '1'; } catch { mustAsk = false; }
        const ownerRole = ['OWNER', 'EXECUTIVE', 'AI_CHAMPION'].includes((d.data.viewer.role || '').toUpperCase());
        if (mustAsk) { setAfterPassword(next); setChapter('password'); } else if (ownerRole && !hasProblem) setChapter('problem'); else setChapter(next);
      }
    } catch {
      setError('Could not load your cards.');
    } finally {
      setLoading(false);
    }
  }, [decideGitPrompt]);
  useEffect(() => { load(); }, [load]);

  // The website is read in the background from the moment the client existed.
  // While the starter cards are on the table, look again a few times so the
  // rail and the deck show the facts as they land, not on the next visit.
  const siteRead = deck ? deck.site : '';
  useEffect(() => {
    if (!siteRead || chapter !== 'starter') return undefined;
    const timers = [6000, 15000, 30000].map((ms) => window.setTimeout(() => { refresh(''); }, ms));
    return () => { timers.forEach((t) => window.clearTimeout(t)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteRead, chapter]);

  // An account the partner opened has a random password. Once the starter
  // cards are done, or the moment the owner wants to leave, one card asks for
  // a password of their own so the next visit works without the link.
  const [needsPassword, setNeedsPassword] = useState<boolean>(() => {
    try { return localStorage.getItem('orbit_set_password') === '1'; } catch { return false; }
  });
  const [afterPassword, setAfterPassword] = useState<'between' | 'done' | 'leave'>('done');
  const passwordCard = useMemo<DeckCardModel>(() => ({
    id: 'set_password', tag: 'confirm', tagLabel: 'So you can come back', title: 'Reset your password.',
    why: 'Your Solution Partner opened this account with a temporary password. Set your own.', input: 'password', noSkip: true,
  }), []);
  const starterCards = useMemo(() => (deck ? toDeckCards(deck, people) : []), [deck, people]);
  const restCards = useMemo(() => (deck ? toDeckCards({ ...deck, cards: deck.rest }, people) : []), [deck, people]);
  const intakeDecks = useMemo<IntakeDeck[]>(() => INTAKE_DECKS
    .map((d) => ({ ...d, cards: restCards.filter((c) => String((c.meta || {}).intake || 'other') === d.id) }))
    .filter((d) => d.cards.length > 0), [restCards]);
  // The intakes finished (or passed over) on this visit, and the one open now.
  const [passed, setPassed] = useState<string[]>([]);
  const [openIntake, setOpenIntake] = useState('');
  const nextIntake = intakeDecks.find((d) => !passed.includes(d.id)) || null;
  const currentIntake = intakeDecks.find((d) => d.id === openIntake) || null;
  const role = deck ? (deck.viewer.role || '').toUpperCase() : '';
  const isOwner = ['OWNER', 'EXECUTIVE', 'AI_CHAMPION'].includes(role);
  const rail = useMemo(() => {
    if (!deck || !isOwner) return null;
    return railFromOverview(deck.site, overview, learned, auto, deck.counts);
  }, [deck, overview, learned, auto, isOwner]);
  const searchTools = useMemo(() => searchToolsFor(), []);
  const contextItems = useMemo<DeckContextItem[]>(() => {
    const site = deck ? deck.site : '';
    if (!site) return added;
    return [{ key: 'site', label: hostOf(site) || site, kind: 'website' }, ...added];
  }, [deck, added]);

  // The last card leads straight into the Room, which is the top of Company
  // Context. No summary screen in between. A role without a Company Context
  // screen lands on its own Home instead, rather than on a route guard.
  let roomPath = '/dashboard';
  if (role === 'OWNER' || role === 'DEPARTMENT_HEAD') roomPath = '/company-context';
  /*
   * The deck is over when there is nothing left to deal, not only when the
   * stack says so.
   *
   * Answering a card reloads the deck, and the reload after the LAST one comes
   * back with an empty hand. That stops this page rendering the stack at all,
   * which unmounts it, and unmounting cancels the pending callback that was
   * going to move the chapter to 'done'. The chapter stayed where it was, the
   * redirect below never fired, and the person sat on "Opening your Room" with
   * nothing opening it. Whether it happened at all came down to whether the
   * last card cleared server-side before the stack's own timer ran, which is
   * why it reproduced on staging and not locally.
   *
   * So the end of the deck is read from the cards, which is the same thing the
   * closing screen below is drawn from. The stack's callback still fires where
   * it can; both arrive at the same place.
   */
  const deckIsOver = (): boolean => {
    if (loading || error || !deck) return false;
    if (chapter === 'password' || chapter === 'problem') return false;
    if (chapter === 'starter' && isOwner && !problemDone) return false;
    if (chapter === 'starter') return starterCards.length === 0 && restCards.length === 0;
    if (chapter === 'between') return nextIntake === null;
    if (chapter === 'rest') return false;
    return true;
  };
  const nothingLeft = deckIsOver();

  useEffect(() => {
    if (!nothingLeft || gitPrompt === 'unknown') return undefined;
    if (gitPrompt === 'show') {
      const t = window.setTimeout(() => { setLeaveTarget(roomPath); setGitPromptOpen(true); }, 600);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => navigate(roomPath, { replace: true }), 900);
    return () => window.clearTimeout(t);
  }, [nothingLeft, roomPath, navigate, gitPrompt]);

  const markGitPromptSeen = () => {
    try { localStorage.setItem(gitPromptKey(clientIdForGit), '1'); } catch { /* storage may be blocked */ }
    setGitPrompt('done');
    setGitPromptOpen(false);
  };
  const leave = (target: string) => {
    if (gitPrompt === 'show') { setLeaveTarget(target); setGitPromptOpen(true); return; }
    navigate(target);
  };

  const refresh = async (justAnswered: string) => {
    try {
      const fresh = await aaiApi.getOrbitDeck('me');
      const after = new Set([...fresh.data.cards, ...fresh.data.rest].map((c) => c.id));
      let shown = starterCards;
      if (chapter === 'rest') shown = currentIntake ? currentIntake.cards : [];
      const gone = shown.map((c) => c.id).filter((id) => !after.has(id) && id !== justAnswered);
      if (gone.length) { setAuto((n) => n + gone.length); setRemoved((r) => [...r, ...gone]); }
      setDeck(fresh.data);
      aaiApi.getOrbitOverview().then((o) => setOverview(o.data)).catch(() => undefined);
    } catch {
      // The deck on screen stays as it is.
    }
  };

  const answerProblem = async (card: DeckCardModel, answer: DeckAnswer): Promise<DeckCardModel[] | void> => {
    const text = String(answer.value || '').trim();
    const field = problemField(card);
    if (field === 'statement') {
      if (!text) return undefined;
      const { data } = await aaiApi.createDeckProblem(text);
      setProblemId(data.id);
      setProblemCards([PAIN_CARD, ...FOLLOW_UP_CARDS]);
      setLearned((l) => [{ text: 'Problem recorded. It is on Projects under Clarity; Continue scoping opens it in the Workspace with everything here.', gold: true }, ...l].slice(0, 6));
      return undefined;
    }
    if (!problemId || !text) return undefined;
    await confirmProblemField(problemId, `p.${field}`, text, 'confirm');
    setLearned((l) => [{ text: `${card.title.replace(/\?$/, '')}: added to the problem.` }, ...l].slice(0, 6));
    return undefined;
  };

  const onAnswer = async (card: DeckCardModel, answer: DeckAnswer) => {
    if (card.id.startsWith(PROBLEM_PREFIX)) {
      try {
        return await answerProblem(card, answer);
      } catch {
        toast.error('Could not save that. Try again.');
        throw new Error('problem failed');
      }
    }
    if (card.input === 'password') {
      try {
        await updateMyPassword({ new_password: String(answer.value || ''), confirm_password: String(answer.value || '') });
        try { localStorage.removeItem('orbit_set_password'); } catch { /* storage may be blocked */ }
        setNeedsPassword(false);
        setLearned((l) => [{ text: 'Password saved. Sign in any time at the login page with your email.', gold: true }, ...l].slice(0, 6));
        toast.success('Password reset. You can sign in without the link from now on.');
      } catch {
        toast.error('Could not save the password. Try again.');
        throw new Error('password failed');
      }
      return;
    }
    try {
      await performAnswer(card, answer);
      setLearned((l) => [learnedLineFor(card, answer), ...l].slice(0, 6));
      await refresh(card.id);
    } catch {
      toast.error('Could not save that. Try again.');
      throw new Error('answer failed');
    }
  };
  const onSkip = async (card: DeckCardModel) => {
    if (card.id.startsWith(PROBLEM_PREFIX)) {
      let line = 'No problem named yet. Add one any time from Projects.';
      if (problemId) line = `Skipped. "${card.title.replace(/\?$/, '')}" is asked again when scoping starts.`;
      setLearned((l) => [{ text: line }, ...l].slice(0, 6));
      return;
    }
    try {
      await performSkip(card);
      setLearned((l) => [{ text: `Skipped. "${card.title.replace(/\?$/, '')}" waits for you on Home.` }, ...l].slice(0, 6));
    } catch {
      toast.error('Could not skip that.');
    }
  };
  const onDocument = async (card: DeckCardModel, file: File) => {
    try {
      const res = await readDocument(file, null);
      setLearned((l) => [{ text: `${file.name} read: ${res.written} facts.`, gold: true }, ...l].slice(0, 6));
      await refresh(card.id);
      return undefined;
    } catch {
      toast.error('Could not read that file.');
      return undefined;
    }
  };

  /* Something handed to Orbit instead of answered: a file, a note, a link. It
     is read into the whole dossier, so the cards it answers leave the deck. */
  const absorb = async (label: string, kind: string, work: () => Promise<{ facts: number; queued: boolean }>) => {
    let read: { facts: number; queued: boolean };
    try {
      read = await work();
    } catch {
      toast.error(`Could not read ${label}.`);
      throw new Error('context failed');
    }
    setAdded((a) => [...a, { key: `${kind}:${a.length}:${label}`, label, kind }]);
    let line = `${label} read: ${read.facts} facts.`;
    if (read.queued) line = `${label} is being read now. What it answers leaves the deck on its own.`;
    setLearned((l) => [{ text: line, gold: true }, ...l].slice(0, 6));
    await refresh('');
  };
  const context: DeckContextState = {
    items: contextItems,
    onFile: (file) => absorb(file.name, 'document', async () => {
      const res = await readDocument(file, null);
      return { facts: res.written, queued: false };
    }),
    onNote: (text) => absorb('Pasted notes', 'note', async () => {
      const res = await ingest('paste', text);
      return { facts: (res.deltas || []).length, queued: res.mode === 'async' };
    }),
    onLink: (link) => absorb(hostOf(link) || link, 'link', async () => {
      const res = await ingest('url', link);
      return { facts: (res.deltas || []).length, queued: res.mode === 'async' };
    }),
  };

  /* The same reach as the rail: the company dossier is the owner's to fill, so
     a person answering only their own cards is not handed a door into it. */
  let sideContext: DeckContextState | null = null;
  if (isOwner) sideContext = context;

  const continueAfterCards = () => {
    let next: Chapter = 'done';
    if (restCards.length > 0) next = 'between';
    if (needsPassword) { setAfterPassword(next === 'between' ? 'between' : 'done'); setChapter('password'); return; }
    setChapter(next);
  };
  const afterStarter = () => {
    if (isOwner && !problemDone) { setChapter('problem'); return; }
    continueAfterCards();
  };
  const afterProblem = () => {
    setProblemDone(true);
    continueAfterCards();
  };
  // One intake's deck is done: back to the card that opens the next one.
  const finishIntake = () => {
    if (openIntake) setPassed((p) => (p.includes(openIntake) ? p : [...p, openIntake]));
    setOpenIntake('');
    setChapter('between');
  };
  const openNextIntake = () => {
    if (!nextIntake) return;
    setOpenIntake(nextIntake.id);
    setChapter('rest');
  };
  /* Answering the last card of an intake reloads the deck without it, which
     unmounts the stack before its own onDone runs (see deckIsOver). */
  useEffect(() => {
    if (chapter !== 'rest' || currentIntake || loading) return;
    if (openIntake) setPassed((p) => (p.includes(openIntake) ? p : [...p, openIntake]));
    setOpenIntake('');
    setChapter('between');
  }, [chapter, currentIntake, openIntake, loading]);
  const pickUpLater = () => {
    if (needsPassword) { setAfterPassword('leave'); setChapter('password'); return; }
    leave('/');
  };
  const passwordSaved = () => {
    if (afterPassword === 'leave') { leave('/'); return; }
    setChapter(afterPassword);
  };

  let title = 'Answer what you know.';
  let subtitle = 'Skip the rest.';
  let known = '';
  if (deck && deck.known) known = ` and has ${deck.known} facts on file`;
  let intro = `Orbit read what it could${known}. Only what nobody could answer is in the deck.`;
  if (role === 'DEPARTMENT_HEAD' || role === 'TEAM_LEAD') {
    title = 'Your department, in a few minutes.';
    let dept = 'your department';
    if (deck && deck.viewer.department_name) dept = deck.viewer.department_name;
    intro = `Your Department intake for ${dept}, then your Person intake. Answer what you know and skip the rest; it waits on your Home.`;
  }
  if (deck && !isOwner && role !== 'DEPARTMENT_HEAD' && role !== 'TEAM_LEAD') {
    title = 'A few questions about your work.';
    intro = 'Your Person intake: questions about you and your own work. Answer what you know and skip the rest; it waits on your Home.';
  }
  if (chapter === 'password') {
    title = 'Reset your password.';
    subtitle = 'So the next visit needs no link.';
    intro = 'You arrived through a link from your Solution Partner. Set your own password now and sign in at the login page with your email from then on.';
  }
  if (chapter === 'problem') {
    title = 'One problem to start with.';
    subtitle = 'Skip it if nothing comes to mind.';
    intro = 'Name a pain point in your own words and Orbit opens it as a problem to scope. Three short follow-ups, all optional, give the Workspace a head start.';
  }
  if (chapter === 'rest' && currentIntake) {
    title = `${currentIntake.label}.`;
    subtitle = 'Same cards, same exits.';
    intro = `${currentIntake.about} Skip anything; it waits on your Home.`;
  }

  const primaryBtn = 'rounded-full bg-[#18191d] px-5 py-2.5 text-[14px] font-semibold text-white';
  const quietBtn = 'rounded-full border border-[#dedede] bg-white px-5 py-2.5 text-[14px] font-semibold text-[#18191d]';

  const body = (() => {
    if (loading) return <p className="text-[13px] text-[#666971]">Dealing your cards</p>;
    if (error || !deck) return <p className="text-[13px] text-[#c8102e]">{error || 'Could not load your cards.'}</p>;
    if (chapter === 'starter' && starterCards.length > 0) {
      return (
        <OrbitDeck
          key="starter"
          cards={starterCards}
          rail={rail}
          context={sideContext}
          removedIds={removed}
          transcribe={transcribeFor(null)}
          searchTools={searchTools}
          onAnswer={onAnswer}
          onSkip={onSkip}
          onDocument={onDocument}
          onDone={afterStarter}
        />
      );
    }
    const problemTurn = chapter === 'problem' || (chapter === 'starter' && isOwner && !problemDone && starterCards.length === 0);
    if (problemTurn) {
      return (
        <OrbitDeck
          key="problem"
          cards={problemCards}
          rail={rail}
          context={sideContext}
          removedIds={[]}
          transcribe={transcribeFor(null)}
          searchTools={searchTools}
          onAnswer={onAnswer}
          onSkip={onSkip}
          onDocument={() => undefined}
          onDone={afterProblem}
        />
      );
    }
    if (chapter === 'password') {
      return (
        <OrbitDeck
          key="password"
          cards={[passwordCard]}
          rail={null}
          onAnswer={onAnswer}
          onSkip={() => undefined}
          onDocument={() => undefined}
          onDone={passwordSaved}
        />
      );
    }
    if (nextIntake && (chapter === 'between' || (chapter === 'starter' && restCards.length > 0 && (!isOwner || problemDone)))) {
      let count = `${nextIntake.cards.length} questions.`;
      if (nextIntake.cards.length === 1) count = 'One question.';
      if (nextIntake.id === 'tool' && deck && deck.per_tool) {
        count = `${nextIntake.cards.length} questions, then ${deck.per_tool} for each tool you name.`;
      }
      let done = 'The starter cards are done';
      if (!isOwner && starterCards.length === 0) done = 'Welcome';
      const last = INTAKE_DECKS.find((d) => d.id === passed[passed.length - 1]);
      if (last) done = `${last.label} is done`;
      return (
        <div className="max-w-[640px] rounded-[16px] border border-[#e7e7e4] bg-white p-7">
          <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#777982]">{done}</p>
          <h2 className="mt-2 text-[24px] font-bold leading-[1.2] text-[#18191d]">{nextIntake.label}: {count}</h2>
          <p className="mt-2 text-[14px] text-[#666971]">{nextIntake.about} About {Math.max(2, Math.ceil(nextIntake.cards.length * 0.5))} minutes. Or pick up later; they wait on your Home and nothing is lost.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" onClick={openNextIntake} className={primaryBtn}>Continue</button>
            <button type="button" onClick={pickUpLater} className={quietBtn}>Pick up later</button>
          </div>
        </div>
      );
    }
    if (chapter === 'rest' && currentIntake) {
      return (
        <OrbitDeck
          key={`rest-${currentIntake.id}`}
          cards={currentIntake.cards}
          rail={rail}
          context={sideContext}
          removedIds={removed}
          transcribe={transcribeFor(null)}
          searchTools={searchTools}
          onAnswer={onAnswer}
          onSkip={onSkip}
          onDocument={onDocument}
          onDone={finishIntake}
        />
      );
    }
    return (
      <div className="max-w-[640px] rounded-[16px] border border-[#e7e7e4] bg-white p-7">
        <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#777982]">Nothing left to ask</p>
        <h2 className="mt-2 text-[24px] font-bold leading-[1.2] text-[#18191d]">Opening your Room.</h2>
        <p className="mt-2 text-[14px] text-[#666971]">Everything you answered is filed with you as the source. What Orbit read waits there for one tap.</p>
      </div>
    );
  })();

  let company = 'your company';
  if (deck && deck.company_name) company = deck.company_name;

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-[#e7e7e4] bg-white/90 px-6 py-3.5 backdrop-blur">
        <img src="/logo-black.png" alt="AgentsAnywhere" className="h-7 w-auto object-contain" />
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-2 rounded-full border border-[#e7e7e4] bg-white px-3 py-1.5 text-[12.5px] text-[#666971] sm:inline-flex"><i className="h-2 w-2 rounded-full bg-[#5da14a]" />Orbit reads only what you gave it.</span>
          {chapter !== 'password' && <button type="button" onClick={pickUpLater} className="rounded-full border border-[#dedede] bg-white px-4 py-2 text-[13px] font-semibold text-[#18191d]">Pick up later</button>}
        </div>
      </header>
      <main className="mx-auto max-w-[1200px] px-6 pb-20 pt-10">
        <div className="mb-6">
          <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#777982]">The Orbit deck · {company}</p>
          <h1 className="m-0 text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-[#18191d] sm:text-[34px]">{title}<br /><span className="text-[#92949d]">{subtitle}</span></h1>
          <p className="mt-3 text-[14px] text-[#666971]">{intro}</p>
        </div>
        {body}
      </main>
      {gitPromptOpen && (
        <ConnectGitPrompt
          clientId={clientIdForGit}
          onLater={() => { markGitPromptSeen(); navigate(leaveTarget, { replace: true }); }}
          onConnect={() => { markGitPromptSeen(); window.setTimeout(() => navigate(leaveTarget, { replace: true }), 300); }}
        />
      )}
    </div>
  );
};

export default DeckPage;
